import { ProductType } from "@happy-legal/graphql-enums";
import { Abonnement, User } from "@happy-legal/graphql-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  InputAdornment,
} from "@mui/material";
import { set } from "lodash";
import React, { MouseEvent, useContext, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
// Use native crypto.randomUUID() instead of uuid package to avoid type issues
import HLCheckboxWithLabel from "../../shared/components/Form/HLCheckboxWithLabel";
import HLSelect from "../../shared/components/Form/HLSelect";
import HLTextField from "../../shared/components/Form/HLTextField";
import { errorMessage } from "../../shared/consts";
import { BuildingKindEnum } from "../../shared/enums";
import { MUTATION_ADD_GT_CASE } from "../../shared/gql/mutations";
import { isAboValid } from "../../shared/helpers";
import { hasDirtyFields } from "../../shared/helpers/hasDirtyFields";
import { CaseContext } from "../../shared/providers/CaseProvider";
import ProfileProvider, {
  ProfileContext,
} from "../../shared/providers/ProfileProvider";
import {
  ADD_CASE,
  GET_ALL_PROFILES,
  UPDATE_PROFILE,
} from "../../shared/reducer";
import { GeneralTenantCaseTypeEnum } from "../enums";
import { formatStreetLine } from "../../shared/utils";
import { notify } from "../../shared/utils/notify";

const defaultValues = {
  id: "",
  firstName: "",
  lastName: "",
  user: {
    email: "",
  },
  rentalObject: {
    id: "",
    address: {
      streetName: "",
      houseNumber: "",
      apartmentNumber: "",
      postalCode: "",
      city: "",
      country: "Deutschland",
    },
    buildingKind: "",
    socialBuilding: false,
    contractDate: "",
    totalRent: "",
  },
};

type TAddGTDialog = { handleCloseDialog: () => void };

const AddGTDialog = (props: TAddGTDialog) => (
  <ProfileProvider>
    <AddGTDialogConsumer {...props} />
  </ProfileProvider>
);

//TODO: Replace with link to admin area of frontend when complete
const AddGTDialogConsumer = ({ handleCloseDialog }: TAddGTDialog) => {
  // Access state and reducer
  const { state: caseState, dispatch: caseDispatch } = useContext(CaseContext);
  const { state: profileState, dispatch: profileDispatch } =
    useContext(ProfileContext);

  const caseData = caseState?.caseData;
  const allProfiles = profileState?.allProfiles;

  // React Router
  const navigate = useNavigate();

  // RHF
  const methods = useForm({ defaultValues, shouldUnregister: true });
  const {
    watch,
    setValue,
    formState: { isSubmitSuccessful },
    reset,
    getValues,
  } = methods;

  //Close dialog and navigate to case after successful submit
  useEffect(() => {
    if (!isSubmitSuccessful || !caseData?.id) return;
    handleCloseDialog();

    navigate(`/mietrechtsanfragen?id=${caseData?.id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful, caseData?.id]);

  // Filter only valid subscribers
  const subscribers = allProfiles?.items?.filter((profile) =>
    isAboValid(profile?.getCurrentAbo as any as Abonnement)
  );

  // Match the selected subscriber via ID
  const ownerSub: string = watch("id");
  const subscriber = subscribers?.find(
    (subscriber) => subscriber?.id === ownerSub
  );

  // Update the name fields to those from backend
  const email: string = watch("user.email");

  useEffect(() => {
    if (!subscriber) return;
    setValue("user.email", (subscriber.user as any as User).email);
  }, [ownerSub]);

  // Update address fields according to that from backend
  const rentalObjectId = watch("rentalObject.id");
  useEffect(() => {
    if (!rentalObjectId) return;

    const currentValuesWithDefaultAddress = set(
      getValues(), // Get current form values
      "rentalObject.address", // and set address
      defaultValues.rentalObject.address // to default
    );

    // Reset form to current values with default address
    reset(currentValuesWithDefaultAddress);
  }, [rentalObjectId]);

  /**
   * Handled outside of reducer because of complex handling of new case
   */
  const handleSubmit = (formData: any) => {
    if (!subscriber) {
      return notify(
        `${errorMessage}\nNo subscriber was found when trying to add a GT case.`
      );
    }

    // Destructure required fields
    const {
      // Remove non-case values
      id: ownerSub,
      user, // eslint-disable-line @typescript-eslint/no-unused-vars
      rentalObject: { id: rentalObjectId, ...rentalObject },
      ...caseValues
    } = formData;

    // Profile state updates occur to slow and therefore we manually create an id
    // if we have not previously loaded a rentalObject
    const generatedRentalObjectId = crypto.randomUUID();

    if (!rentalObjectId) {
      const profilePayload = {
        rentalObjects: {
          id: generatedRentalObjectId,
          ...rentalObject,
        },
      };

      // Add names to profile
      [
        "rentalObjects.address.firstName",
        "rentalObjects.tenants[0].firstName",
      ].forEach((field) => set(profilePayload, field, subscriber.firstName));
      [
        "rentalObjects.address.lastName",
        "rentalObjects.tenants[0].lastName",
      ].forEach((field) => set(profilePayload, field, subscriber.lastName));

      // Only update profile if no rentalObject exists on profile
      profileDispatch({
        type: UPDATE_PROFILE,
        id: ownerSub,
        payload: profilePayload,
      });
    }

    const casePayload = {
      ownerSub: ownerSub,
      ...caseValues,
      rentalObjectId: rentalObjectId || generatedRentalObjectId,
      referrer: {
        referringPartner: (subscriber.getCurrentAbo as any as Abonnement)
          .partnerId,
        id: ownerSub,
      },
      productType: ProductType.OTHER,
    };

    //Add case
    caseDispatch({
      type: ADD_CASE,
      mutation: MUTATION_ADD_GT_CASE,
      payload: casePayload,
    });

    /**
     * Async issue means that the case id is not updated in time
     * to be in this submission function. Responses
     *
     * In the isSubmitSuccessful useEffect hook further up
     * the user is navigated to /case?id=caseId
     */
  };

  // Retrieve profiles from backend, fitlered by name
  const getProfiles = (e: MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // Prevent submission
    e.preventDefault();

    const filter = { user: { email } };
    profileDispatch({ type: GET_ALL_PROFILES, filter });
  };

  return (
    <Dialog open onClose={handleCloseDialog}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <HLTextField
                  label="E-Mail-Addresse"
                  name="user.email"
                  type="email"
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                {/* 
								//@ts-ignore TODO: Material UI types are incorrect and require href - create issue on Github */}
                <Button
                  variant="contained"
                  disableElevation
                  type="submit"
                  color="primary"
                  onClick={getProfiles}
                  disabled={!email}
                >
                  Finden
                </Button>
              </Grid>
              {/* Show when name and subscribers found */}
              {email && !!subscribers?.length && (
                <Grid size={{ xs: 12 }}>
                  <HLSelect
                    name="id"
                    label="Abonnent"
                    options={subscribers.map((profile) => {
                      const firstName = profile.firstName;
                      const lastName = profile.lastName;
                      return {
                        label: `${firstName} ${lastName}`,
                        value: profile.id,
                      };
                    })}
                  />
                </Grid>
              )}
              {/* When subscriber found */}
              {subscriber && (
                <>
                  {/* with prior rentalObject */}
                  {subscriber?.rentalObjects?.length ? (
                    <Grid size={{ xs: 12 }}>
                      <HLSelect
                        name="rentalObject.id"
                        label="Adresse"
                        options={subscriber?.rentalObjects?.map(
                          (rentalObject: any) => {
                            const {
                              streetName,
                              houseNumber,
                              postalCode,
                              city,
                            } = rentalObject.address;
                            return {
                              label: `${formatStreetLine(streetName, houseNumber)}, ${postalCode} ${city}`,
                              value: rentalObject.id,
                            };
                          }
                        )}
                      />
                    </Grid>
                  ) : (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <HLTextField
                          label="Straße"
                          name="rentalObject.address.streetName"
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <HLTextField
                          label="Hausnr."
                          name="rentalObject.address.houseNumber"
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <HLTextField
                          label="Adresszusatz"
                          name="rentalObject.address.apartmentNumber"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <HLTextField
                          label="Postleitzahl"
                          name="rentalObject.address.postalCode"
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <HLTextField
                          label="Stadt"
                          name="rentalObject.address.city"
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <HLTextField
                          label="Land"
                          name="rentalObject.address.country"
                          required
                        />
                      </Grid>
                    </>
                  )}
                  <Grid size={{ xs: 12 }}>
                    <Divider />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <HLSelect
                      label="Produkt-Typ"
                      name="generalTenantCaseType"
                      required
                      options={Object.entries(GeneralTenantCaseTypeEnum)
                        .filter(
                          ([value]) =>
                            value !== "BETRIEBSKOSTENABRECHNUNG" &&
                            value !== "KAUTION"
                        )
                        .map(([value, label]) => ({
                          label,
                          value,
                        }))}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <HLTextField
                      label="Beschreibung der Anfrage"
                      name="description"
                      multiline
                      rows={3}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Divider />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <HLTextField
                      label="Seit wann besteht das Mietverhältnis"
                      name="rentalObject.contractDate"
                      type="date"
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <HLTextField
                      label="Aktuelle Warmmiete"
                      name="rentalObject.totalRent"
                      type="number"
                      inputProps={{
                        min: "0.00",
                        step: "0.01",
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">&euro;</InputAdornment>
                        ),
                      }}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <HLSelect
                      label="Gebäudeart"
                      name="rentalObject.buildingKind"
                      required
                      options={Object.entries(BuildingKindEnum).map(
                        ([value, label]) => ({
                          label,
                          value,
                        })
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <HLCheckboxWithLabel
                      label="Es handelt sich um Sozialwohnungen, ein Pflegeheim oder einem Studentenwohnheim?"
                      name="rentalObject.socialBuilding"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Abbrechen
            </Button>
            <Button
              type="submit"
              color="primary"
              // Allowed when dirtyFields and retrieved profile id
              disabled={!hasDirtyFields(methods) && !ownerSub}
            >
              Speichern
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default AddGTDialog;
